import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import BookingGroupRow from '../../components/BookingGroups.jsx';
import { groupByBooking, useCollapsedGroups } from '../../utils/bookingGroups.js';
import { money } from '../../utils/money.js';

/** Format a dashboard total. When the drafts span several currencies the totals
 * are not one amount, so no single code is claimed — the figure is flagged as mixed. */
function total(n, currency) {
  if (n == null) return '—';
  if (currency) return money(n, currency, { decimals: 0 });
  return `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })} (mixed currencies)`;
}
function monthLabel(m) {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString(undefined, { month: 'short', year: '2-digit' });
}

const DONUT_COLORS = ['#0d6efd', '#198754', '#fd7e14', '#6f42c1', '#dc3545', '#20c997'];

export default function CommercialDashboardPage() {
  const [options, setOptions] = useState({ customers: [], ports: [] });
  const [customer, setCustomer] = useState('');
  const [portId, setPortId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [data, setData] = useState(null);
  // 8992: billing records are listed beneath their booking; each booking folds away.
  const [isAgingCollapsed, toggleAgingGroup] = useCollapsedGroups();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [applying, setApplying] = useState(false);

  const loadDashboard = useCallback(async (filters) => {
    const res = await api.getCommercialDashboard(filters || {});
    setData(res);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [opts] = await Promise.all([api.getCommercialOptions(), loadDashboard()]);
        setOptions(opts || { customers: [], ports: [] });
      } catch (err) {
        setLoadError(err.message || 'Failed to load commercial dashboard.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadDashboard]);

  const applyFilters = async () => {
    setApplying(true);
    try {
      await loadDashboard({ customer, portId, from, to });
    } catch (err) {
      setLoadError(err.message || 'Failed to apply filters.');
    } finally {
      setApplying(false);
    }
  };

  // Reset every filter and reload the unfiltered view (8865).
  const clearFilters = async () => {
    setCustomer('');
    setPortId('');
    setFrom('');
    setTo('');
    setApplying(true);
    try {
      await loadDashboard({});
    } catch (err) {
      setLoadError(err.message || 'Failed to clear filters.');
    } finally {
      setApplying(false);
    }
  };

  const maxTrend = useMemo(
    () => Math.max(1, ...((data?.trend || []).map((t) => t.revenue + t.affiliateCost) || [1])),
    [data]
  );

  // Single currency of every draft in view, or null when they are mixed.
  const ccy = data?.totals ? (data.totals.currency === undefined ? 'USD' : data.totals.currency) : 'USD';

  const donutGradient = useMemo(() => {
    const slices = data?.revenueByCustomer || [];
    let acc = 0;
    const stops = slices.map((s, i) => {
      const start = acc;
      acc += s.sharePct;
      return `${DONUT_COLORS[i % DONUT_COLORS.length]} ${start}% ${acc}%`;
    });
    return stops.length ? `conic-gradient(${stops.join(', ')})` : 'conic-gradient(#e9ecef 0% 100%)';
  }, [data]);

  return (
    <div className="container-fluid py-4" data-testid="commercial-dashboard-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-chart-line me-2" aria-hidden="true" />
                Commercial Revenues &amp; Costs
              </h3>
              <p className="text-muted mb-0 small">
                Revenues, affiliate subcontracting costs, owned-tug margins and billing aging.
              </p>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="commercial-load-error">
          {loadError}
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label htmlFor="f-customer" className="form-label small fw-bold text-muted">
                Customer / Shipping Line
              </label>
              <select
                id="f-customer"
                className="form-select form-select-sm"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                data-testid="filter-customer"
              >
                <option value="">All customers</option>
                {options.customers.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="f-port" className="form-label small fw-bold text-muted">
                Port
              </label>
              <select
                id="f-port"
                className="form-select form-select-sm"
                value={portId}
                onChange={(e) => setPortId(e.target.value)}
                data-testid="filter-port"
              >
                <option value="">All ports</option>
                {options.ports.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="f-from" className="form-label small fw-bold text-muted">
                From
              </label>
              <input
                id="f-from"
                type="date"
                className="form-control form-control-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="filter-from"
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="f-to" className="form-label small fw-bold text-muted">
                To
              </label>
              <input
                id="f-to"
                type="date"
                className="form-control form-control-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="filter-to"
              />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm flex-grow-1"
                onClick={applyFilters}
                disabled={applying}
                data-testid="apply-filters"
              >
                {applying && (
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                )}
                <i className="fas fa-filter me-1" aria-hidden="true" />
                Apply
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={clearFilters}
                disabled={applying}
                title="Clear all filters"
                data-testid="clear-filters"
              >
                <i className="fas fa-xmark me-1" aria-hidden="true" />
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        data && (
          <>
            {/* KPI cards */}
            <div className="row g-3 mb-4" data-testid="commercial-kpis">
              <div className="col-6 col-lg-3">
                <div className="border rounded p-3 bg-light text-center h-100">
                  <div className="text-muted small text-uppercase">Total Revenue</div>
                  <div className="h4 mb-0 text-success" data-testid="kpi-revenue">
                    {ccy || !data.totals.revenueByCurrency?.length
                      ? total(data.totals.totalRevenue, ccy)
                      : data.totals.revenueByCurrency.map((r) => (
                          <div key={r.currency} data-testid="kpi-revenue-currency" data-currency={r.currency}>
                            {money(r.amount, r.currency, { decimals: 0 })}
                          </div>
                        ))}
                  </div>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="border rounded p-3 bg-light text-center h-100">
                  <div className="text-muted small text-uppercase">Affiliate Cost</div>
                  <div className="h4 mb-0 text-danger" data-testid="kpi-affiliate-cost">
                    {total(data.totals.totalAffiliateCost, ccy)}
                  </div>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="border rounded p-3 bg-light text-center h-100">
                  <div className="text-muted small text-uppercase">Net Margin</div>
                  <div className="h4 mb-0 text-primary" data-testid="kpi-margin">
                    {total(data.totals.netMargin, ccy)}
                    <span className="text-muted small"> ({data.totals.marginPct}%)</span>
                  </div>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="border rounded p-3 bg-light text-center h-100">
                  <div className="text-muted small text-uppercase">Pending Billings</div>
                  <div className="h4 mb-0 text-warning" data-testid="kpi-pending">
                    {data.totals.pendingBillings}
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Monthly revenue & cost stacked chart */}
              <div className="col-lg-7 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white pt-4 border-bottom-0">
                    <h6 className="mb-0 text-secondary">Monthly Revenue, Affiliate Cost &amp; Margin</h6>
                  </div>
                  <div className="card-body" data-testid="trend-chart">
                    {data.trend.length === 0 && <p className="text-muted small mb-0">No data for the filter.</p>}
                    <div className="d-flex align-items-end gap-3" style={{ height: 220 }}>
                      {data.trend.map((t) => {
                        const totalH = ((t.revenue + t.affiliateCost) / maxTrend) * 180;
                        const revH = ((t.revenue) / maxTrend) * 180;
                        const costH = ((t.affiliateCost) / maxTrend) * 180;
                        return (
                          <div
                            key={t.month}
                            className="text-center flex-grow-1"
                            data-testid="trend-bar"
                            data-month={t.month}
                            title={`${monthLabel(t.month)} — Revenue ${total(t.revenue, ccy)}, Affiliate ${total(
                              t.affiliateCost,
                              ccy
                            )}, Margin ${total(t.margin, ccy)}`}
                          >
                            <div
                              className="d-flex flex-column-reverse mx-auto"
                              style={{ height: totalH || 2, width: 36 }}
                            >
                              <div className="bg-success" style={{ height: revH }} />
                              <div className="bg-danger" style={{ height: costH }} />
                            </div>
                            <div className="small text-muted mt-1">{monthLabel(t.month)}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="small text-muted mt-2">
                      <span className="badge bg-success me-1">&nbsp;</span> Owned revenue
                      <span className="badge bg-danger ms-3 me-1">&nbsp;</span> Affiliate cost
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue by customer donut */}
              <div className="col-lg-5 mb-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white pt-4 border-bottom-0">
                    <h6 className="mb-0 text-secondary">Revenue by Customer / Shipping Line</h6>
                  </div>
                  <div className="card-body" data-testid="customer-donut">
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          background: donutGradient,
                          flex: '0 0 120px',
                          position: 'relative',
                        }}
                        aria-hidden="true"
                      >
                        <div
                          style={{
                            position: 'absolute',
                            inset: 30,
                            background: '#fff',
                            borderRadius: '50%',
                          }}
                        />
                      </div>
                      <ul className="list-unstyled mb-0 ms-3 small flex-grow-1">
                        {data.revenueByCustomer.length === 0 && (
                          <li className="text-muted">No revenue for the filter.</li>
                        )}
                        {data.revenueByCustomer.map((c, i) => (
                          <li
                            key={c.customer}
                            className="d-flex justify-content-between align-items-center mb-1"
                            data-testid="donut-legend"
                            data-customer={c.customer}
                          >
                            <span>
                              <span
                                className="d-inline-block me-2"
                                style={{
                                  width: 10,
                                  height: 10,
                                  background: DONUT_COLORS[i % DONUT_COLORS.length],
                                  borderRadius: 2,
                                }}
                              />
                              {c.customer}
                            </span>
                            <strong>{c.sharePct}%</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing aging */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h6 className="mb-0 text-secondary">Billing Status Aging Report</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0" data-testid="aging-table">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Booking / Job</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Issue Date</th>
                        <th>Days Pending</th>
                        <th>Sync Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.aging.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4" data-testid="aging-empty">
                            No billing records for the filter.
                          </td>
                        </tr>
                      )}
                      {/* 8992: each booking once, with the billing record of each of its jobs beneath it */}
                      {groupByBooking(data.aging, (a) => a.bookingId ?? a.bookingRef).map((g) => {
                        const collapsed = isAgingCollapsed(g.key);
                        const totals = new Map();
                        for (const a of g.rows) totals.set(a.currency, (totals.get(a.currency) || 0) + a.amount);
                        const pending = g.rows.filter((a) => a.syncStatus === 'Pending');
                        const group = { ...g, ref: g.first.bookingRef };
                        return (
                          <React.Fragment key={g.key}>
                            <BookingGroupRow group={group} colSpan={6} collapsed={collapsed} onToggle={toggleAgingGroup}>
                              <span data-testid="booking-group-detail">
                                {g.first.customer}
                                {g.first.vesselName ? ` · ${g.first.vesselName}` : ''}
                                {' · '}
                                <strong className="text-body" data-testid="booking-group-total">
                                  {[...totals.entries()].map(([ccy, amt]) => money(amt, ccy)).join(' + ')}
                                </strong>
                                {' · '}
                                {pending.length
                                  ? `${pending.length} pending, oldest ${Math.max(...pending.map((a) => a.daysPending))} days`
                                  : 'all synced'}
                              </span>
                            </BookingGroupRow>
                            {!collapsed &&
                              g.rows.map((a) => (
                                <tr key={a.draftId ?? a.bookingRef} data-testid="aging-row" data-ref={a.bookingRef} data-job-no={a.jobNo || undefined}>
                                  <td className="ps-4">
                                    <span className="fw-bold d-block" data-testid="aging-job">
                                      {a.jobNo ? `${a.jobNo} · ${a.jobName}` : a.bookingRef}
                                    </span>
                                    <small className="text-muted">
                                      {a.tugName || '—'}
                                      {a.ticketNo != null ? ` · Ticket No. ${a.ticketNo}` : ''}
                                    </small>
                                  </td>
                                  <td className="small">{a.customer}</td>
                                  <td>{money(a.amount, a.currency)}</td>
                                  <td className="small">{new Date(a.issueDate).toLocaleDateString()}</td>
                                  <td>{a.daysPending}</td>
                                  <td>
                                    <span className={`badge ${a.syncStatus === 'Synced' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                      {a.syncStatus}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
